import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, WritableSignal, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChartData, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { CardModule } from 'primeng/card';
import { MultiSelectModule } from 'primeng/multiselect';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RegistrationNumbersForDatesResult } from 'src/app/api/types/registration';
import { UiModule } from 'src/app/ui/ui.module';
import { DashboardDataService } from 'src/app/routed-modules/mission-control/services/dashboard-data.service';

interface YearData {
  year: number;
  data: number[];
}

@Component({
  selector: 'app-original-registration-stats-graph',
  standalone: true,
  imports: [CommonModule, BaseChartDirective, CardModule, MultiSelectModule, FormsModule, UiModule],
  templateUrl: './original-registration-stats-graph.component.html',
  styleUrl: './original-registration-stats-graph.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OriginalRegistrationStatsGraphComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  private currentYear = new Date().getFullYear();
  
  // Generate year options from 2008 to current year + 1
  yearOptions = Array.from(
    { length: this.currentYear - 2008 + 2 }, 
    (_, i) => this.currentYear + 1 - i
  ).sort((a, b) => b - a); // Sort descending

  // Initialize with current year and previous year selected
  selectedYearsValue = signal<number[]>([this.currentYear, this.currentYear - 1]);

  // Store registration data
  private registrationData: WritableSignal<RegistrationNumbersForDatesResult[]> = signal([]);
  
  // Loading state
  isLoading = signal<boolean>(true);

  // Year data map will be populated from real data
  private yearDataMap = new Map<number, YearData>();

  chartData = signal<ChartData<'line', number[], string>>({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: this.getSelectedYearsData()
  });

  chartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toLocaleString();
            }
            return label;
          }
        }
      },
      title: {
        display: true,
        text: "Original Registrations (All Trip Years)"
      }
    },
    hover: {
      mode: 'x'
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 500,
          callback: function(value) {
            return value.toLocaleString();
          }
        }
      },
      x: {
        beginAtZero: true,
        ticks: {
          display: true
        }
      }
    }
  };

  constructor(private dashboardDataService: DashboardDataService) {

    effect(() => {
      const currentData = this.dashboardDataService.registrationGraphData();
      if (currentData && currentData.length > 0) {
        this.loadGraphData(currentData);
      }
    })

  }

  ngOnInit() {
    // Initialize data loading
    this.dashboardDataService.initializeData();

  }

  private loadGraphData(graphData: any[]) {
    this.isLoading.set(true);
    
    // Process the graph data and update chart
    this.processGraphData(graphData);
    
    this.isLoading.set(false);
    
    // Force chart update after a short delay to ensure DOM is ready
    setTimeout(() => {
      this.chartData.update(current => ({ ...current }));
    }, 100);
  }

  private processGraphData(data: any[]) {
    // Store the registration data
    this.registrationData.set(data);
    
    // Update the year data map
    this.updateYearDataMap();
    
    // Update the chart data
    this.updateChartData();
  }

  private updateYearDataMap() {
    const data = this.registrationData();
    const yearMap = new Map<number, number[]>();

    // Initialize arrays for each year
    data.forEach(item => {
      if (item.year) {
        if (!yearMap.has(item.year)) {
          yearMap.set(item.year, Array(12).fill(0));
        }
        if (item.month && item.month >= 1 && item.month <= 12) {
          const monthArray = yearMap.get(item.year);
          if (monthArray) {
            monthArray[item.month - 1] = item.totalOriginal;
          }
        }
      }
    });

    // Convert to YearData format
    this.yearDataMap = new Map(
      Array.from(yearMap.entries()).map(([year, data]) => [
        year,
        { year, data }
      ])
    );
  }

  private updateChartData() {
    // Match ColdFusion: Fixed 12-month labels (registration stats always show full calendar year)
    this.chartData.set({
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      datasets: this.getSelectedYearsData()
    });
  }

  onYearSelectionChange(selectedYears: number[]) {
    this.selectedYearsValue.set(selectedYears);
    this.updateChartData();
  }

  private getSelectedYearsData() {
    return this.selectedYearsValue().map(year => {
      const yearData = this.yearDataMap.get(year);
      let styling: any = {};

      if (year === this.currentYear) {
        // Match ColdFusion: Current year - blue colors
        styling = {
          backgroundColor: "rgba(151,187,205,0.2)",
          borderColor: "rgba(28, 132, 198, 0.7)",
          pointBorderColor: "#fff",
          pointBackgroundColor: "rgba(28, 132, 198, 0.7)",
          pointHoverBackgroundColor: "rgba(28, 132, 198, 0.7)",
          pointHoverBorderColor: "rgba(28, 132, 198, 0.7)"
        };
      } else if (year === this.currentYear - 1) {
        // Match ColdFusion: Previous year - gray colors
        styling = {
          backgroundColor: "rgba(150,150,150,0.1)",
          borderColor: "rgba(150,150,150,.8)",
          pointBackgroundColor: "rgba(150,150,150,.8)",
          pointBorderColor: "#fff",
          pointHoverBackgroundColor: "rgba(150,150,150,.8)",
          pointHighlightStroke: "rgba(150,150,150,.8)"
        };
      } else {
        // Match ColdFusion: Older years - gray with opacity
        const opacity = 0.5;
        styling = {
          backgroundColor: "rgba(150,150,150,0.1)",
          borderColor: `rgba(150,150,150,${opacity})`,
          pointBackgroundColor: `rgba(150,150,150,${opacity})`,
          pointBorderColor: "#fff",
          pointHoverBackgroundColor: `rgba(150,150,150,${opacity})`,
          pointHighlightStroke: `rgba(150,150,150,${opacity})`
        };
      }

      return {
        data: yearData?.data ?? [],
        label: `${year}`, // Match ColdFusion: Use year number as label
        ...styling,
        tension: 0.4,
        fill: true
      };
    }).sort((a, b) => Number(b.label) - Number(a.label)); // Sort by year descending
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
} 